import arAgent from '@hero/lang/locales/ar/m-agent.json';
import enAgent from '@hero/lang/locales/en/m-agent.json';
import urAgent from '@hero/lang/locales/ur/m-agent.json';
import type { Language } from '@hero/schema';

/**
 * User-facing copy the agent's code still writes — card chrome, buttons,
 * the picker, errors. It lives in `@hero/lang` `locales/<code>/m-agent.json`
 * under `channel`, one file per language; the model writes everything
 * else. See `docs/ai/multi-language.md` § Agent copy.
 */

type CopyTree = { [key: string]: string | CopyTree };

type Leaves<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${Prefix}${K}` : Leaves<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

/** `Intl.PluralRules` categories — Arabic uses all six, English and Urdu `one` / `other`. */
const PLURAL_CATEGORIES = ['zero', 'one', 'two', 'few', 'many', 'other'] as const;
type PluralCategory = (typeof PLURAL_CATEGORIES)[number];

/** Nodes whose children are plural forms, e.g. `tools.billing.hello_deal_line`. */
type PluralNodes<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? never
    : Exclude<keyof T[K], PluralCategory> extends never
    ? T[K] extends { other: string }
      ? `${Prefix}${K}`
      : never
    : PluralNodes<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

/**
 * Every key of the English `channel` tree, e.g. `picker.choose_language`,
 * plus each plural node (read with `{ count }`).
 */
export type AgentCopyKey =
  | Leaves<(typeof enAgent)['channel']>
  | PluralNodes<(typeof enAgent)['channel']>;

/** A node whose keys are all plural categories and that has `other`. */
export const isPluralNode = (node: unknown): node is Partial<Record<PluralCategory, string>> & { other: string } =>
  Boolean(node) &&
  typeof node === 'object' &&
  typeof (node as { other?: unknown }).other === 'string' &&
  Object.keys(node as object).every(key => (PLURAL_CATEGORIES as readonly string[]).includes(key));

export const AGENT_COPY: Record<Language, CopyTree> = {
  ar: arAgent.channel,
  en: enAgent.channel,
  ur: urAgent.channel,
};

/**
 * The string at `key`. A plural node picks its form for `count` by the
 * language's own rules — Arabic says «وثيقة» for 1 and 11+, «وثيقتان» for
 * 2, «وثائق» for 3–10 — falling back to `other`.
 */
const lookup = (
  tree: CopyTree | undefined,
  key: string,
  language: Language,
  count: number | undefined,
): string | undefined => {
  let node: string | CopyTree | undefined = tree;
  for (const part of key.split('.')) {
    if (!node || typeof node === 'string') return undefined;
    node = node[part];
  }
  if (typeof node === 'string') return node;
  if (!isPluralNode(node)) return undefined;
  const category = count === undefined ? 'other' : (new Intl.PluralRules(language).select(count) as PluralCategory);
  return node[category] ?? node.other;
};

type MissingReporter = (input: { language: Language; key: string }) => void;

let reportMissing: MissingReporter = () => undefined;
const reported = new Set<string>();

/** The backend wires Sentry here at boot; called once per language and key. */
export const setAgentCopyMissingReporter = (reporter: MissingReporter): void => {
  reportMissing = reporter;
};

const interpolate = (text: string, vars: Record<string, string | number> | undefined): string =>
  vars ? text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match)) : text;

/**
 * Copy for `key` in `language`, with `{{var}}` filled. A key missing in
 * that language falls back to English and is reported once; a key
 * missing in English too returns the key itself (the completeness spec
 * keeps that from shipping).
 */
export const agentCopy = (
  language: Language,
  key: AgentCopyKey,
  vars?: Record<string, string | number>,
): string => {
  const count = typeof vars?.count === 'number' ? vars.count : undefined;
  const own = lookup(AGENT_COPY[language], key, language, count);
  if (own !== undefined) return interpolate(own, vars);
  const marker = `${language}:${key}`;
  if (!reported.has(marker)) {
    reported.add(marker);
    reportMissing({ language, key });
  }
  return interpolate(lookup(AGENT_COPY.en, key, 'en', count) ?? key, vars);
};

/**
 * Every key of a copy tree — for the completeness spec. A plural node is
 * one key: languages carry different forms (Arabic six, English two).
 */
export const copyKeysOf = (tree: CopyTree, prefix = ''): string[] =>
  Object.entries(tree).flatMap(([key, value]) =>
    typeof value === 'string' || isPluralNode(value)
      ? [`${prefix}${key}`]
      : copyKeysOf(value, `${prefix}${key}.`),
  );

/** `{{name}}` placeholders in one string, sorted. */
export const placeholdersOf = (text: string): string[] =>
  [...text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map(match => match[1]).sort();
